import { useState, useEffect, useRef } from "react";
import BarcodeDetectionService, {
  type BarcodeDetectionResult,
} from "../services/BarcodeDetectionService";
import { type CameraState } from "./useCameraInitialization";
import type { CameraSlotConfig } from "../services/StorageService";

interface UseBarcodeDetectionOptions {
  scanFrequency?: number;
  cooldownPeriod?: number;
  onBarcodeDetected?: (
    cameraIndex: number,
    barcode: string,
    format: string,
    isValid: boolean
  ) => void;
  validateBarcode?: (barcode: string) => boolean;
  onBarcodeCleared?: (cameraIndex: number) => void;
  slotConfigs?: [CameraSlotConfig, CameraSlotConfig, CameraSlotConfig]; // Per-slot enhancement configs
}

interface UseBarcodeDetectionResult {
  isBarcodeApiSupported: boolean;
  initializeDetectors: () => void;
  startDetection: (
    cameraIndex: number,
    videoRef: React.RefObject<HTMLVideoElement | null>
  ) => void;
  stopDetection: (cameraIndex: number) => void;
  stopAllDetection: () => void;
}

export const useBarcodeDetection = (
  cameraInitializationState: CameraState[],
  videoRefs: React.RefObject<HTMLVideoElement | null>[],
  options: UseBarcodeDetectionOptions = {}
): UseBarcodeDetectionResult => {
  const {
    scanFrequency = 300,
    onBarcodeDetected,
    validateBarcode = () => true,
    onBarcodeCleared,
    slotConfigs,
  } = options;

  const [isBarcodeApiSupported, setIsBarcodeApiSupported] =
    useState<boolean>(false);
  const barcodeDetectorRef = useRef<BarcodeDetectionService[]>([]);
  const activeDetectorsRef = useRef<Set<number>>(new Set());

  const detectionTimeouts = useRef<(NodeJS.Timeout | null)[]>([
    null,
    null,
    null,
  ]);
  const lastDetectedCodes = useRef<(string | null)[]>([null, null, null]); // Track last detected codes
  const detectionIntervals = useRef<(NodeJS.Timeout | null)[]>([
    null,
    null,
    null,
  ]);

  // Initialize barcode detection
  useEffect(() => {
    const supported = BarcodeDetectionService.isApiSupported();
    setIsBarcodeApiSupported(supported);

    if (supported) {
      initializeDetectors();
    }

    return () => {
      stopAllDetection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize barcode detectors
  const initializeDetectors = () => {
    if (BarcodeDetectionService.isApiSupported()) {
      // Create a detector for each possible camera
      barcodeDetectorRef.current = Array(cameraInitializationState.length)
        .fill(null)
        .map(() => new BarcodeDetectionService());

      // Get supported formats for debug info
      BarcodeDetectionService.getSupportedFormats().then((formats) => {
        console.log("Supported barcode formats:", formats);
      });
    }
  };

  // Clear barcode after timeout if not detected again
  const clearBarcodeAfterTimeout = (cameraIndex: number) => {
    if (detectionTimeouts.current[cameraIndex]) {
      clearTimeout(detectionTimeouts.current[cameraIndex]!);
    }

    detectionTimeouts.current[cameraIndex] = setTimeout(() => {
      // Only clear if this code is still the last detected one
      if (lastDetectedCodes.current[cameraIndex]) {
        // console.log(
        //   `Clearing barcode for camera ${cameraIndex} - no longer detected`
        // );
        lastDetectedCodes.current[cameraIndex] = null;
        // Trigger a "clear" event to update UI
        onBarcodeCleared?.(cameraIndex);
      }
    }, 2000); // Clear after 1 second of no detection (changed from 2000)
  };

  // Handle barcode detection results
  const handleBarcodeDetected = (
    cameraIndex: number,
    results: BarcodeDetectionResult[]
  ) => {
    if (results.length === 0) return;

    // Use the first detected barcode
    const barcode = results[0].code;
    const format = results[0].format;
    // const currentTime = Date.now();

    // Validate the barcode
    const isValid = validateBarcode(barcode);

    // console.log(
    //   `Camera ${
    //     cameraIndex + 1
    //   } detected barcode. Format: ${format}, Value: ${barcode}, Valid: ${isValid}`
    // );

    // Call the callback if provided
    if (onBarcodeDetected) {
      onBarcodeDetected(cameraIndex, barcode, format, isValid);
    }

    // Update the tracking ref
    lastDetectedCodes.current[cameraIndex] = barcode;

    // Reset the clear timeout since we detected the code again
    clearBarcodeAfterTimeout(cameraIndex);
  };

  // Start detection for a specific camera
  const startDetection = (
    cameraIndex: number,
    videoRef: React.RefObject<HTMLVideoElement | null>
  ) => {
    if (
      isBarcodeApiSupported &&
      !activeDetectorsRef.current.has(cameraIndex) &&
      videoRef.current
    ) {
      console.log(`Starting barcode detection for camera ${cameraIndex + 1}`);

      // Get slot config for this camera
      const slotConfig = slotConfigs?.[cameraIndex];

      barcodeDetectorRef.current[cameraIndex].startContinuousScan(
        videoRef.current,
        (results) => handleBarcodeDetected(cameraIndex, results),
        scanFrequency,
        {
          slotConfig, // Pass slot-specific enhancement config
        }
      );

      activeDetectorsRef.current.add(cameraIndex);
    }
  };

  // Stop detection for a specific camera
  const stopDetection = (cameraIndex: number) => {
    if (activeDetectorsRef.current.has(cameraIndex)) {
      barcodeDetectorRef.current[cameraIndex].stopContinuousScan();
      activeDetectorsRef.current.delete(cameraIndex);
    }
  };

  // Stop all detection
  const stopAllDetection = () => {
    detectionIntervals.current.forEach((interval, index) => {
      if (interval) {
        clearInterval(interval);
        detectionIntervals.current[index] = null;
      }
      if (detectionTimeouts.current[index]) {
        clearTimeout(detectionTimeouts.current[index]!);
        detectionTimeouts.current[index] = null;
      }
    });
    lastDetectedCodes.current = [null, null, null];
  };

  // Auto-start/stop detection based on camera state changes
  useEffect(() => {
    cameraInitializationState.forEach((state, index) => {
      // If camera is ready and not already detecting, start detection
      if (
        state === "ready" &&
        !activeDetectorsRef.current.has(index) &&
        videoRefs[index].current
      ) {
        startDetection(index, videoRefs[index]);
      }
      // If camera is no longer ready but was detecting, stop detection
      else if (state !== "ready" && activeDetectorsRef.current.has(index)) {
        stopDetection(index);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraInitializationState, videoRefs]);

  return {
    isBarcodeApiSupported,
    initializeDetectors,
    startDetection,
    stopDetection,
    stopAllDetection,
  };
};
